// src/services/api.service.ts
import { publicApi } from '../utils/publicApi';
import axios from 'axios';
import { Season, Permission } from '../utils/types';

// Determine API URL based on where the app is being accessed from
const getApiUrl = () => {
  // If accessing from production URL
  if (window.location.hostname === 'access.tms.hockey') {
    return '';  // Use same domain, Nginx will proxy /api requests
  }
  // For local development
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:4000';
  }
  // Default fallback (direct IP access)
  return 'http://167.99.35.10:4000';
};

// Backend API instance for Monday.com operations
const backendApi = axios.create({
  baseURL: getApiUrl(),
  withCredentials: false
});

class ApiService {
  async fetchSeasons(orgId?: string): Promise<Season[]> {
    try {
      // Build query params
      let queryParams = 'start_date=gt.2025-01-01';
      if (orgId) {
        queryParams = `organization_id=eq.${orgId}&${queryParams}`;
      }
      
      const response = await publicApi.get(`/public/seasons?${queryParams}`);
      
      if (!response || !response.data) {
        throw new Error('No response from server');
      }

      const seasonData: Season[] = Array.isArray(response.data) ? response.data : [];
      console.log(`Fetched ${seasonData.length} seasons for org ${orgId || 'all'}`);
      return seasonData;
    } catch (err: any) {
      console.error('Error fetching seasons:', err);
      throw new Error(err.message || 'Failed to fetch seasons');
    }
  }

  async fetchTeamsForSeason(seasonId: string): Promise<any[]> {
    try {
      const response = await publicApi.get(`/public/seasons/${seasonId}/teams`);
      return response.data || [];
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      return [];
    }
  }

  // Competition Manager permissions (Board 1)
  async fetchPermissions(seasonId: string): Promise<Permission[]> {
    try {
      console.log(`Loading permissions for season: ${seasonId}`);
      const response = await backendApi.get(`/api/permissions/${seasonId}`);
      return response.data || [];
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      return [];
    }
  }

  async savePermissions(permissions: Permission[]): Promise<void> {
    try {
      console.log('Saving permissions:', permissions);
      const response = await backendApi.post('/api/permissions', { permissions });
      if (!response.data.success) {
        throw new Error('Save operation failed');
      }
    } catch (err: any) {
      console.error('Error saving permissions:', err);
      throw new Error(err.response?.data?.details || err.message || 'Failed to save permissions');
    }
  }

  // Team Manager permissions (Board 2)
  async fetchTeamPermissions(teamId: string): Promise<Permission[]> {
    try {
      console.log(`Loading team permissions for team: ${teamId}`);
      const response = await backendApi.get(`/api/team-permissions/${teamId}`);
      return response.data || [];
    } catch (err: any) {
      console.error('Error fetching team permissions:', err);
      return [];
    }
  }

  async saveTeamPermissions(permissions: Permission[]): Promise<void> {
    try {
      console.log('Saving team permissions:', permissions);
      const response = await backendApi.post('/api/team-permissions', { permissions });
      if (!response.data.success) {
        throw new Error('Save operation failed');
      }
    } catch (err: any) {
      console.error('Error saving team permissions:', err);
      throw new Error(err.response?.data?.details || err.message || 'Failed to save team permissions');
    }
  }

  async deletePermission(itemId: string): Promise<void> {
    try {
      await backendApi.delete(`/api/permissions/${itemId}`);
    } catch (err: any) {
      console.error('Error deleting permission:', err);
      throw new Error(err.message || 'Failed to delete permission');
    }
  }
}

const apiService = new ApiService();
export default apiService;